<template>
  <div>
    <Head title="Mi Catálogo Digital" />

    <!-- Header & Link al Catálogo -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 rounded-3xl text-white shadow-xl shadow-orange-100 mb-8">
      <div class="flex items-center gap-4">
        <div class="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-lg">
          <i class="fas fa-store text-white text-2xl" />
        </div>
        <div>
          <span class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-white/20 text-white border border-white/30">Canal de Venta Online</span>
          <h1 class="font-extrabold text-2xl sm:text-3xl leading-tight mt-0.5">Mi Catálogo Digital</h1>
          <p class="text-orange-100 text-sm mt-0.5">Personaliza la apariencia, contacto, WhatsApp y banners de tu tienda online</p>
        </div>
      </div>
      <div class="flex items-center gap-3 flex-wrap">
        <a
          :href="`/catalogo/${account.username || account.id}`" target="_blank"
          class="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-orange-600 font-bold rounded-2xl hover:bg-orange-50 hover:scale-105 active:scale-95 transition-all text-sm shadow-lg flex-shrink-0"
        >
          <i class="fas fa-external-link-alt text-xs" />
          Abrir Tienda Online
        </a>
      </div>
    </div>

    <!-- Grid 2 columnas: formulario + preview -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-8">
      <!-- ── Columna izquierda (formulario + banners, 2/3) ── -->
      <div class="xl:col-span-2 space-y-8">
        <!-- 1. WHATSAPP PARA PEDIDOS (campo más crítico) -->
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-8 py-5 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-green-50/50 to-white">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <i class="fab fa-whatsapp text-green-600 text-lg" />
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="font-bold text-gray-900 text-lg">WhatsApp para Pedidos</h3>
                  <span class="px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-[10px] font-black uppercase tracking-wider">Esencial</span>
                </div>
                <p class="text-xs text-gray-400">Tus clientes enviarán el resumen de su carrito a este número</p>
              </div>
            </div>
          </div>
          <form class="p-8" @submit.prevent="update">
            <div class="max-w-xl">
              <label class="block text-sm font-bold text-gray-700 mb-2">Número de WhatsApp Corporativo *</label>
              <div class="relative">
                <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-green-500">
                  <i class="fab fa-whatsapp text-xl" />
                </span>
                <input
                  v-model="form.catalog_whatsapp_number"
                  type="tel"
                  class="w-full pl-11 pr-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400 font-mono"
                  placeholder="+584241234567"
                />
              </div>
              <p class="text-xs text-gray-400 mt-2">
                Escribe el número con código internacional. Ejemplo: <code class="bg-gray-100 px-1 py-0.5 rounded font-mono">+584241234567</code>
              </p>

              <div v-if="form.catalog_whatsapp_number" class="mt-4 p-4 bg-green-50 rounded-2xl border border-green-100 flex items-center justify-between gap-3">
                <div class="flex items-center gap-2 text-sm text-green-800">
                  <i class="fas fa-check-circle text-green-500" />
                  <span>WhatsApp configurado: <strong>{{ form.catalog_whatsapp_number }}</strong></span>
                </div>
                <a
                  :href="`https://wa.me/${form.catalog_whatsapp_number.replace(/[^0-9]/g,'')}`"
                  target="_blank" class="text-xs font-bold text-green-700 hover:text-green-900 underline flex items-center gap-1"
                >
                  Probar enlace <i class="fas fa-arrow-right text-[10px]" />
                </a>
              </div>
              <div v-else class="mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-center gap-3">
                <i class="fas fa-exclamation-triangle text-amber-500" />
                <p class="text-xs text-amber-800 font-medium">Sin este número, los clientes no podrán finalizar pedidos por WhatsApp.</p>
              </div>
            </div>

            <div class="flex justify-end mt-6 pt-4 border-t border-gray-50">
              <loading-button :loading="form.processing" class="btn-orange shadow-lg shadow-orange-100" type="submit">
                <i class="fas fa-save mr-2" /> Guardar WhatsApp
              </loading-button>
            </div>
          </form>
        </div>

        <!-- 2. IDENTIDAD VISUAL & COLORES -->
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-8 py-5 border-b border-gray-50 flex items-center gap-3 bg-gradient-to-r from-gray-50/50 to-white">
            <div class="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <i class="fas fa-palette text-orange-600" />
            </div>
            <div>
              <h3 class="font-bold text-gray-900 text-lg">Identidad Visual y Colores</h3>
              <p class="text-xs text-gray-400">Personaliza la paleta cromática para que coincida con tu marca</p>
            </div>
          </div>
          <form class="p-8 space-y-6" @submit.prevent="update">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Color principal -->
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2">
                  Color Principal de la Marca *
                </label>
                <div class="flex items-center gap-3">
                  <input
                    v-model="form.catalog_primary_color"
                    type="color"
                    class="w-12 h-12 rounded-xl border-2 border-gray-200 cursor-pointer p-0.5"
                  />
                  <div class="flex-1">
                    <input
                      v-model="form.catalog_primary_color"
                      type="text"
                      class="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="#F97316"
                    />
                  </div>
                </div>
                <p class="text-xs text-gray-400 mt-1.5">Define el color de botones, badges y barra superior</p>
              </div>

              <!-- Color de fondo -->
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2">
                  Color de Fondo de la Página
                </label>
                <div class="flex items-center gap-3">
                  <input
                    v-model="form.catalog_bg_color"
                    type="color"
                    class="w-12 h-12 rounded-xl border-2 border-gray-200 cursor-pointer p-0.5"
                  />
                  <input
                    v-model="form.catalog_bg_color"
                    type="text"
                    class="flex-1 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="#F9FAFB"
                  />
                </div>
                <p class="text-xs text-gray-400 mt-1.5">Recomendado: tono suave o blanco</p>
              </div>
            </div>

            <!-- Colores avanzados (colapsables) -->
            <div class="border-t border-gray-50 pt-4">
              <button
                type="button" class="flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-orange-600 transition-colors"
                @click="showAdvancedColors = !showAdvancedColors"
              >
                <i :class="showAdvancedColors ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="text-xs w-3 text-orange-500" />
                <span>Colores avanzados (tarjetas, ofertas, textos)</span>
              </button>
              <div v-if="showAdvancedColors" class="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 p-5 bg-gray-50/70 rounded-2xl border border-gray-100">
                <div v-for="colorField in advancedColorFields" :key="colorField.key">
                  <label class="block text-xs font-semibold text-gray-600 mb-1.5">{{ colorField.label }}</label>
                  <div class="flex items-center gap-2">
                    <input v-model="form[colorField.key]" type="color" class="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer p-0.5 flex-shrink-0" />
                    <input v-model="form[colorField.key]" type="text" class="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-orange-400" />
                  </div>
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between gap-3 pt-4 border-t border-gray-50">
              <button
                type="button" class="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1.5 transition-colors"
                @click="resetCatalogColors"
              >
                <i class="fas fa-undo text-xs" /> Restaurar colores por defecto
              </button>
              <loading-button :loading="form.processing" class="btn-orange shadow-lg shadow-orange-100" type="submit">
                <i class="fas fa-save mr-2" /> Guardar Colores
              </loading-button>
            </div>
          </form>
        </div>

        <!-- 3. BANNERS / SLIDES PROMOCIONALES -->
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-8 py-5 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-gray-50/50 to-white">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <i class="fas fa-images text-purple-600" />
              </div>
              <div>
                <h3 class="font-bold text-gray-900 text-lg">Banners y Slides del Catálogo</h3>
                <p class="text-xs text-gray-400">Imágenes rotativas en la cabecera de tu tienda digital</p>
              </div>
            </div>
            <button class="btn-orange shadow-sm text-sm" @click="showSlideModal = true">
              <i class="fas fa-plus mr-1.5" /> Nuevo Banner
            </button>
          </div>

          <div class="p-8">
            <div v-if="slidesList.length === 0" class="flex flex-col items-center justify-center py-10 text-gray-400 bg-gray-50/30 rounded-2xl border-2 border-dashed border-gray-100">
              <i class="fas fa-images text-3xl text-gray-300 mb-2" />
              <p class="font-bold text-sm text-gray-600">No hay banners configurados</p>
              <p class="text-xs text-gray-400 mt-0.5">Agrega imágenes promocionales para destacar ofertas en tu tienda.</p>
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="slide in slidesList" :key="slide.id"
                class="flex items-center justify-between p-4 rounded-2xl border border-gray-100 bg-white hover:border-orange-200 transition-all"
              >
                <div class="flex items-center gap-4">
                  <div class="w-20 h-12 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center border border-gray-200 flex-shrink-0">
                    <img v-if="slide.image_desktop" :src="`/storage/${slide.image_desktop}`" class="w-full h-full object-cover" />
                    <i v-else class="fas fa-image text-gray-300" />
                  </div>
                  <div>
                    <h4 class="font-bold text-gray-900 text-sm">{{ slide.title }}</h4>
                    <p class="text-xs text-gray-400 line-clamp-1">{{ slide.description || 'Sin descripción' }}</p>
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <button
                    :class="slide.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'"
                    class="px-3 py-1 rounded-full text-xs font-bold transition-colors"
                    @click="toggleSlideStatus(slide)"
                  >
                    {{ slide.is_active ? 'Activo' : 'Pausado' }}
                  </button>
                  <button class="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center" title="Eliminar" @click="deleteSlide(slide)">
                    <i class="fas fa-trash text-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. INFORMACIÓN DE CONTACTO -->
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-8 py-5 border-b border-gray-50 flex items-center gap-3 bg-gradient-to-r from-gray-50/50 to-white">
            <div class="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <i class="fas fa-address-book text-blue-600" />
            </div>
            <div>
              <h3 class="font-bold text-gray-900 text-lg">Información de Contacto</h3>
              <p class="text-xs text-gray-400">Datos visibles para tus clientes al pie del catálogo</p>
            </div>
          </div>
          <form class="p-8" @submit.prevent="update">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <text-input
                v-model="form.catalog_phone" :error="form.errors.catalog_phone"
                label="Teléfono de Atención" placeholder="+58 (424) 123-4567" class="[&_input]:rounded-xl"
              />
              <text-input
                v-model="form.catalog_email" :error="form.errors.catalog_email"
                type="email" label="Correo Electrónico del Catálogo" placeholder="ventas@tienda.com" class="[&_input]:rounded-xl"
              />
              <text-input
                v-model="form.catalog_address" :error="form.errors.catalog_address"
                label="Dirección de la Tienda / Retiro" placeholder="Caracas, Venezuela" class="md:col-span-2 [&_input]:rounded-xl"
              />
              <text-input
                v-model="form.catalog_schedule" :error="form.errors.catalog_schedule"
                label="Horario de Atención al Cliente" placeholder="Lunes a Sábado: 8:00 AM - 6:00 PM" class="md:col-span-2 [&_input]:rounded-xl"
              />
            </div>
            <div class="flex justify-end mt-6 pt-4 border-t border-gray-50">
              <loading-button :loading="form.processing" class="btn-orange shadow-lg shadow-orange-100" type="submit">
                <i class="fas fa-save mr-2" /> Guardar Contacto
              </loading-button>
            </div>
          </form>
        </div>

        <!-- 5. REDES SOCIALES -->
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-8 py-5 border-b border-gray-50 flex items-center gap-3 bg-gradient-to-r from-gray-50/50 to-white">
            <div class="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
              <i class="fas fa-share-alt text-pink-600" />
            </div>
            <div>
              <h3 class="font-bold text-gray-900 text-lg">Redes Sociales</h3>
              <p class="text-xs text-gray-400">Enlaces a tus perfiles oficiales</p>
            </div>
          </div>
          <form class="p-8" @submit.prevent="update">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2">Instagram</label>
                <div class="relative">
                  <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-pink-500">
                    <i class="fab fa-instagram text-lg" />
                  </span>
                  <input
                    v-model="form.catalog_instagram_url"
                    class="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                    placeholder="https://instagram.com/tutienda"
                  />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2">Facebook</label>
                <div class="relative">
                  <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-600">
                    <i class="fab fa-facebook text-lg" />
                  </span>
                  <input
                    v-model="form.catalog_facebook_url"
                    class="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="https://facebook.com/tutienda"
                  />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2">Twitter / X</label>
                <div class="relative">
                  <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-400">
                    <i class="fab fa-twitter text-lg" />
                  </span>
                  <input
                    v-model="form.catalog_twitter_url"
                    class="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="https://x.com/tutienda"
                  />
                </div>
              </div>
            </div>

            <div class="flex justify-end mt-6 pt-4 border-t border-gray-50">
              <loading-button :loading="form.processing" class="btn-orange shadow-lg shadow-orange-100" type="submit">
                <i class="fas fa-save mr-2" /> Guardar Redes
              </loading-button>
            </div>
          </form>
        </div>
      </div>

      <!-- ── Columna derecha: Preview en vivo interactivo (1/3) ── -->
      <div class="xl:col-span-1">
        <div class="sticky top-6 space-y-6">
          <!-- Card de Enlace Compartible -->
          <div class="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                <i class="fas fa-link text-orange-500 text-sm" />
              </div>
              <h3 class="font-bold text-gray-900 text-sm">Enlace Público de tu Catálogo</h3>
            </div>
            <p class="text-xs text-gray-400 mb-3">Comparte este link en tus redes, biografía de Instagram o estados de WhatsApp:</p>
            <div class="flex items-center gap-2">
              <code class="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 flex-1 truncate font-mono">
                {{ catalogShareUrl }}
              </code>
              <button
                type="button" class="px-3.5 py-2.5 rounded-xl bg-orange-500 text-white hover:bg-orange-600 flex items-center justify-center flex-shrink-0 transition-all font-bold text-xs shadow-sm"
                @click="copyCatalogUrl"
              >
                <i :class="urlCopied ? 'fas fa-check' : 'fas fa-copy'" class="mr-1" />
                {{ urlCopied ? '¡Copiado!' : 'Copiar' }}
              </button>
            </div>
          </div>

          <!-- Mockup Live Preview -->
          <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 class="font-bold text-gray-900 text-sm">Vista Previa en Tiempo Real</h3>
                <p class="text-[11px] text-gray-400">Aspecto de tu catálogo digital para los clientes</p>
              </div>
              <span class="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
            </div>
            <div class="p-5">
              <!-- Mockup del catálogo -->
              <div class="rounded-2xl overflow-hidden border border-gray-200 shadow-md">
                <!-- Header mockup -->
                <div
                  class="h-16 flex items-center justify-between px-4"
                  :style="{ backgroundColor: form.catalog_primary_color }"
                >
                  <div class="flex items-center gap-2.5">
                    <div class="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center overflow-hidden">
                      <img v-if="account.logo" :src="account.logo" class="w-full h-full object-contain" />
                      <i v-else class="fas fa-store text-white text-xs" />
                    </div>
                    <span class="text-white text-xs font-extrabold truncate max-w-[120px]">{{ account.name || 'Mi Tienda' }}</span>
                  </div>
                  <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <i class="fas fa-shopping-bag text-white text-xs" />
                  </div>
                </div>

                <!-- Banner mockup -->
                <div class="h-20 bg-gradient-to-r from-gray-800 to-gray-950 flex items-center justify-center px-4 text-center">
                  <div>
                    <p class="text-[11px] font-black text-white">¡Nuevos Productos Disponibles!</p>
                    <p class="text-[9px] text-gray-300">Envíos directos con pedidos vía WhatsApp</p>
                  </div>
                </div>

                <!-- Chips categorías mock -->
                <div class="flex gap-1.5 px-3 py-2 overflow-hidden" :style="{ backgroundColor: form.catalog_bg_color }">
                  <span
                    class="px-2.5 py-1 rounded-full text-white text-[11px] font-bold flex-shrink-0"
                    :style="{ backgroundColor: form.catalog_primary_color }"
                  >Todos</span>
                  <span class="px-2.5 py-1 rounded-full text-[11px] text-gray-500 bg-white border border-gray-200 flex-shrink-0">Destacados</span>
                  <span class="px-2.5 py-1 rounded-full text-[11px] text-gray-500 bg-white border border-gray-200 flex-shrink-0">Ofertas</span>
                </div>

                <!-- Cards productos mock -->
                <div class="grid grid-cols-2 gap-2 p-3" :style="{ backgroundColor: form.catalog_bg_color }">
                  <div
                    v-for="n in 4" :key="n"
                    class="rounded-xl overflow-hidden shadow-sm border border-gray-100 bg-white"
                  >
                    <div class="h-16 bg-gray-50 flex items-center justify-center">
                      <i class="fas fa-box-open text-gray-300 text-xl" />
                    </div>
                    <div class="p-2">
                      <div class="h-2 bg-gray-200 rounded mb-1.5 w-3/4" />
                      <div class="flex items-center justify-between">
                        <span class="text-xs font-extrabold" :style="{ color: form.catalog_primary_color }">${{ n * 5 }}.00</span>
                        <div
                          class="w-5 h-5 rounded-full flex items-center justify-center text-white"
                          :style="{ backgroundColor: form.catalog_primary_color }"
                        >
                          <i class="fas fa-plus text-[8px]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- WhatsApp footer mock -->
                <div class="p-3 bg-white border-t border-gray-100 flex items-center justify-between text-xs">
                  <div class="flex items-center gap-1.5 text-green-600 font-bold">
                    <i class="fab fa-whatsapp" />
                    <span>Pedidos directos</span>
                  </div>
                  <span class="text-[10px] text-gray-400">Realgestión</span>
                </div>
              </div>

              <!-- Botón abrir catálogo real -->
              <a
                :href="`/catalogo/${account.username || account.id}`" target="_blank"
                class="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-white text-sm font-bold transition-all shadow-md hover:opacity-90"
                :style="{ backgroundColor: form.catalog_primary_color }"
              >
                <i class="fas fa-external-link-alt text-xs" />
                Ver mi catálogo en vivo
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para nuevo banner/slide -->
    <div v-if="showSlideModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <div class="px-8 py-5 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
              <i class="fas fa-plus" />
            </div>
            <div>
              <h3 class="font-bold text-gray-900 text-lg">Nuevo Banner</h3>
              <p class="text-xs text-gray-400">Agrega una imagen promocional para tu catálogo</p>
            </div>
          </div>
          <button class="text-gray-400 hover:text-gray-600" @click="showSlideModal = false">
            <i class="fas fa-times" />
          </button>
        </div>

        <form class="p-8 space-y-4" @submit.prevent="createSlide">
          <text-input v-model="slideForm.title" label="Título del Banner *" placeholder="Ej: Gran Oferta de Temporada" class="[&_input]:rounded-xl" required />
          <text-input v-model="slideForm.description" label="Descripción / Subtítulo" placeholder="Ej: Hasta 30% de descuento en todos los productos" class="[&_input]:rounded-xl" />
          <text-input v-model="slideForm.button_text" label="Texto del Botón" placeholder="Ej: Comprar Ahora" class="[&_input]:rounded-xl" />
          <text-input v-model="slideForm.button_link" label="Enlace del Botón" placeholder="Ej: /catalogo/1" class="[&_input]:rounded-xl" />

          <div>
            <label class="block text-sm font-bold text-gray-700 mb-2">Imagen del Banner *</label>
            <input type="file" accept="image/*" class="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" required @change="e => slideForm.image_desktop = e.target.files[0]" />
            <p class="text-[10px] text-gray-400 mt-1">Recomendado: 1200×400px en formato JPG, PNG o WEBP.</p>
          </div>

          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-50">
            <button type="button" class="px-5 py-2.5 text-sm font-bold text-gray-400 hover:text-gray-600" @click="showSlideModal = false">
              Cancelar
            </button>
            <loading-button :loading="savingSlide" class="btn-orange shadow-md" type="submit">
              Crear Banner
            </loading-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {

  components: {
    Head,
    Link,
    TextInput,
    LoadingButton,
  },
  layout: Layout,

  props: {
    account: {
      type: Object,
      required: true,
    },
    slides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      slidesList: [...this.slides],
      showAdvancedColors: false,
      showSlideModal: false,
      savingSlide: false,
      urlCopied: false,

      form: this.$inertia.form({
        _method: 'put',
        name: this.account.name,
        username: this.account.username,
        reasonName: this.account.reasonName || '',
        // Catalog colors
        catalog_primary_color: this.account.catalog_primary_color || '#F97316',
        catalog_secondary_color: this.account.catalog_secondary_color || '#EA580C',
        catalog_offer_color: this.account.catalog_offer_color || '#EF4444',
        catalog_featured_color: this.account.catalog_featured_color || '#10B981',
        catalog_text_color: this.account.catalog_text_color || '#111827',
        catalog_text_light_color: this.account.catalog_text_light_color || '#6B7280',
        catalog_bg_color: this.account.catalog_bg_color || '#F9FAFB',
        catalog_card_bg_color: this.account.catalog_card_bg_color || '#FFFFFF',
        // Catalog contact info
        catalog_phone: this.account.catalog_phone || '',
        catalog_email: this.account.catalog_email || '',
        catalog_address: this.account.catalog_address || '',
        catalog_schedule: this.account.catalog_schedule || '',
        // Catalog social media
        catalog_facebook_url: this.account.catalog_facebook_url || '',
        catalog_instagram_url: this.account.catalog_instagram_url || '',
        catalog_twitter_url: this.account.catalog_twitter_url || '',
        catalog_whatsapp_number: this.account.catalog_whatsapp_number || '',
      }),

      slideForm: this.$inertia.form({
        title: '',
        description: '',
        button_text: '',
        button_link: '',
        image_desktop: null,
        is_active: true,
      }),
    }
  },

  computed: {
    catalogShareUrl() {
      if (typeof window !== 'undefined') {
        return `${window.location.origin}/catalogo/${this.account.username || this.account.id}`
      }
      return `/catalogo/${this.account.username || this.account.id}`
    },
    advancedColorFields() {
      return [
        { key: 'catalog_secondary_color', label: 'Color Secundario' },
        { key: 'catalog_offer_color', label: 'Color Ofertas' },
        { key: 'catalog_featured_color', label: 'Color Destacados' },
        { key: 'catalog_text_color', label: 'Color Texto' },
        { key: 'catalog_text_light_color', label: 'Texto Suave' },
        { key: 'catalog_card_bg_color', label: 'Color Tarjetas' },
      ]
    },
  },

  methods: {
    update() {
      this.form.post(`/account/${this.account.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          if (this.$toast) {
            this.$toast.success('Configuración de catálogo guardada correctamente')
          }
        },
        onError: (errors) => {
          if (this.$toast) {
            const firstError = Object.values(errors)[0]
            this.$toast.error(firstError || 'Ocurrió un error al guardar')
          }
        },
      })
    },

    copyCatalogUrl() {
      navigator.clipboard.writeText(this.catalogShareUrl).then(() => {
        this.urlCopied = true
        setTimeout(() => { this.urlCopied = false }, 2500)
      }).catch(() => {
        const el = document.createElement('textarea')
        el.value = this.catalogShareUrl
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        this.urlCopied = true
        setTimeout(() => { this.urlCopied = false }, 2500)
      })
    },

    resetCatalogColors() {
      if (confirm('¿Restaurar los colores por defecto del catálogo?')) {
        this.form.catalog_primary_color = '#F97316'
        this.form.catalog_secondary_color = '#EA580C'
        this.form.catalog_offer_color = '#EF4444'
        this.form.catalog_featured_color = '#10B981'
        this.form.catalog_text_color = '#111827'
        this.form.catalog_text_light_color = '#6B7280'
        this.form.catalog_bg_color = '#F9FAFB'
        this.form.catalog_card_bg_color = '#FFFFFF'
      }
    },

    createSlide() {
      this.savingSlide = true
      this.slideForm.post('/catalog-slides', {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => {
          this.showSlideModal = false
          this.slideForm.reset()
          this.savingSlide = false
          this.$inertia.reload({ only: ['slides'] })
        },
        onError: () => {
          this.savingSlide = false
        },
      })
    },

    toggleSlideStatus(slide) {
      this.$inertia.post(`/catalog-slides/${slide.id}/toggle-status`, {}, {
        preserveScroll: true,
        onSuccess: () => {
          slide.is_active = !slide.is_active
        },
      })
    },

    deleteSlide(slide) {
      if (confirm('¿Estás seguro de eliminar este banner?')) {
        this.$inertia.delete(`/catalog-slides/${slide.id}`, {
          preserveScroll: true,
          onSuccess: () => {
            this.slidesList = this.slidesList.filter(s => s.id !== slide.id)
          },
        })
      }
    },
  },
}
</script>
